import { __decorate, __extends } from "tslib";
import { attachShadowTemplate, coerceBoolean, coerceNumber, CustomElement, ensureChild, FoundationProperty, upgradeProperty } from '@tyler-components-web/core';
import { CalendarComponent } from '../calendar/calendar';
import { DialogComponent } from '../dialog';
import { IconButtonComponent } from '../icon-button';
import { PopupComponent } from '../popup';
import { ScaffoldComponent } from '../scaffold';
import { ToolbarComponent } from '../toolbar';
import { DatepickerAdapter } from './datepicker-adapter';
import { DATEPICKER_CONSTANTS } from './datepicker-constants';
import { DatepickerFoundation } from './datepicker-foundation';
import { IconComponent, TylerIconRegistry } from '../icon';
import { tylIconClose, tylIconInsertInvitation } from '@tylertech/tyler-icons/standard';
import { ButtonComponent } from '../button';
import { coerceNumberArray } from '../core';
var template = require('./datepicker.html');
var styles = require('./datepicker.css');
/**
 * The custom element class behind the `<tcw-datepicker>` element.
 */
var DatepickerComponent = /** @class */ (function (_super) {
    __extends(DatepickerComponent, _super);
    function DatepickerComponent() {
        var _this = _super.call(this) || this;
        TylerIconRegistry.define([tylIconClose, tylIconInsertInvitation]);
        attachShadowTemplate(_this, DATEPICKER_CONSTANTS.elementName, template, styles);
        _this._foundation = new DatepickerFoundation(new DatepickerAdapter(_this));
        return _this;
    }
    Object.defineProperty(DatepickerComponent, "observedAttributes", {
        get: function () {
            return [
                DATEPICKER_CONSTANTS.attributes.VALUE,
                DATEPICKER_CONSTANTS.attributes.MIN,
                DATEPICKER_CONSTANTS.attributes.MAX,
                DATEPICKER_CONSTANTS.attributes.OPEN,
                DATEPICKER_CONSTANTS.attributes.DISABLED,
                DATEPICKER_CONSTANTS.attributes.MASKED,
                DATEPICKER_CONSTANTS.attributes.MASK_FORMAT,
                DATEPICKER_CONSTANTS.attributes.SHOW_MASK_FORMAT,
                DATEPICKER_CONSTANTS.attributes.USE_CHAR_MASK,
                DATEPICKER_CONSTANTS.attributes.VALUE_MODE,
                DATEPICKER_CONSTANTS.attributes.POPUP_CLASSES,
                DATEPICKER_CONSTANTS.attributes.DIALOG_WIDTH_THRESHOLD,
                DATEPICKER_CONSTANTS.attributes.ALLOW_INVALID_DATE,
                DATEPICKER_CONSTANTS.attributes.SHOW_TODAY,
                DATEPICKER_CONSTANTS.attributes.SHOW_CLEAR,
                DATEPICKER_CONSTANTS.attributes.DISABLED_DAYS_OF_WEEK,
            ];
        },
        enumerable: true,
        configurable: true
    });
    DatepickerComponent.prototype.initializedCallback = function () {
        upgradeProperty(this, 'value');
        upgradeProperty(this, 'min');
        upgradeProperty(this, 'max');
        upgradeProperty(this, 'blockedDates');
        upgradeProperty(this, 'open');
        upgradeProperty(this, 'parseCallback');
        upgradeProperty(this, 'formatCallback');
        upgradeProperty(this, 'prepareMaskCallback');
        upgradeProperty(this, 'popupClasses');
        upgradeProperty(this, 'disabled');
        upgradeProperty(this, 'masked');
        upgradeProperty(this, 'maskFormat');
        upgradeProperty(this, 'showMaskFormat');
        upgradeProperty(this, 'useCharMask');
        upgradeProperty(this, 'valueMode');
        upgradeProperty(this, 'dialogWidthThreshold');
        upgradeProperty(this, 'notifyInputValueChanges');
        upgradeProperty(this, 'allowInvalidDate');
        upgradeProperty(this, 'showToday');
        upgradeProperty(this, 'showClear');
        upgradeProperty(this, 'disabledDaysOfWeek');
        upgradeProperty(this, 'disableDayCallback');
    };
    DatepickerComponent.prototype.connectedCallback = function () {
        var _this = this;
        if (this.querySelector(DATEPICKER_CONSTANTS.selectors.INPUT)) {
            this._foundation.initialize();
        }
        else {
            ensureChild(this, DATEPICKER_CONSTANTS.selectors.INPUT).then(function () { return _this._foundation.initialize(); });
        }
    };
    DatepickerComponent.prototype.disconnectedCallback = function () {
        this._foundation.disconnect();
    };
    DatepickerComponent.prototype.attributeChangedCallback = function (name, oldValue, newValue) {
        switch (name) {
            case DATEPICKER_CONSTANTS.attributes.VALUE:
                this.value = newValue;
                break;
            case DATEPICKER_CONSTANTS.attributes.MIN:
                this.min = newValue;
                break;
            case DATEPICKER_CONSTANTS.attributes.MAX:
                this.max = newValue;
                break;
            case DATEPICKER_CONSTANTS.attributes.OPEN:
                this.open = this.hasAttribute(DATEPICKER_CONSTANTS.attributes.OPEN);
                break;
            case DATEPICKER_CONSTANTS.attributes.POPUP_CLASSES:
                this.popupClasses = newValue;
                break;
            case DATEPICKER_CONSTANTS.attributes.DISABLED:
                this.disabled = this.hasAttribute(DATEPICKER_CONSTANTS.attributes.DISABLED);
                break;
            case DATEPICKER_CONSTANTS.attributes.MASKED:
                this.masked = coerceBoolean(newValue);
                break;
            case DATEPICKER_CONSTANTS.attributes.SHOW_MASK_FORMAT:
                this.showMaskFormat = coerceBoolean(newValue);
                break;
            case DATEPICKER_CONSTANTS.attributes.MASK_FORMAT:
                this.maskFormat = newValue;
                break;
            case DATEPICKER_CONSTANTS.attributes.USE_CHAR_MASK:
                this.useCharMask = coerceBoolean(newValue);
                break;
            case DATEPICKER_CONSTANTS.attributes.VALUE_MODE:
                this.valueMode = newValue;
                break;
            case DATEPICKER_CONSTANTS.attributes.DIALOG_WIDTH_THRESHOLD:
                this.dialogWidthThreshold = coerceNumber(newValue);
                break;
            case DATEPICKER_CONSTANTS.attributes.ALLOW_INVALID_DATE:
                this.allowInvalidDate = this.hasAttribute(DATEPICKER_CONSTANTS.attributes.ALLOW_INVALID_DATE);
                break;
            case DATEPICKER_CONSTANTS.attributes.SHOW_TODAY:
                this.showToday = this.hasAttribute(DATEPICKER_CONSTANTS.attributes.SHOW_TODAY);
                break;
            case DATEPICKER_CONSTANTS.attributes.SHOW_CLEAR:
                this.showClear = this.hasAttribute(DATEPICKER_CONSTANTS.attributes.SHOW_CLEAR);
                break;
            case DATEPICKER_CONSTANTS.attributes.DISABLED_DAYS_OF_WEEK:
                this.disabledDaysOfWeek = coerceNumberArray(newValue);
                break;
        }
    };
    Object.defineProperty(DatepickerComponent.prototype, "value", {
        /** Gets/sets the date value. */
        get: function () {
            return this._foundation.value;
        },
        set: function (value) {
            this._foundation.value = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "min", {
        /** Gets/sets the minimum date the calendar will allow. */
        get: function () {
            return this._foundation.min;
        },
        set: function (value) {
            this._foundation.min = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "max", {
        /** Gets/sets the maximum date the calendar will allow. */
        get: function () {
            return this._foundation.max;
        },
        set: function (value) {
            this._foundation.max = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "blockedDates", {
        /** Gets/sets the dates that are restricted from being selected. */
        get: function () {
            return this._foundation.blockedDates;
        },
        set: function (value) {
            this._foundation.blockedDates = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "open", {
        /** Gets/sets whether the datepicker is open. */
        get: function () {
            return this._foundation.open;
        },
        set: function (value) {
            this._foundation.open = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "popupClasses", {
        /** Gets/sets the CSS classes that are applied to the popup element. */
        get: function () {
            return this._foundation.popupClasses;
        },
        set: function (value) {
            this._foundation.popupClasses = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "disabled", {
        /** Gets/sets whether the datepicker is disabled or not. */
        get: function () {
            return this._foundation.disabled;
        },
        set: function (value) {
            this._foundation.disabled = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "masked", {
        /** Gets/sets whether the input mask is applied or not. */
        get: function () {
            return this._foundation.masked;
        },
        set: function (value) {
            this._foundation.masked = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "maskFormat", {
        /** Gets/sets the mask format that displayed in the input. Default is `MM/DD/YYYY` */
        get: function () {
            return this._foundation.maskFormat;
        },
        set: function (value) {
            this._foundation.maskFormat = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "showMaskFormat", {
        /** Gets/sets whether the mask format is displayed in the input or not. Only applies if `masked` is `true`. */
        get: function () {
            return this._foundation.showMaskFormat;
        },
        set: function (value) {
            this._foundation.showMaskFormat = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "useCharMask", {
        /**
         * Gets/sets whether the mask format uses the default placeholder (`__/__/___`) or the block
         * identifier (`MM/DD/YYYY`). Only pertains if `showMaskFormat` is `true.
         * @deprecated This property should not be used, and will be removed in a future release.
         */
        get: function () {
            return this._foundation.useCharMask;
        },
        set: function (value) {
            this._foundation.useCharMask = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "valueMode", {
        /** Gets/sets the type for the `value` property and `tcw-datepicker-change` event. */
        get: function () {
            return this._foundation.valueMode;
        },
        set: function (value) {
            this._foundation.valueMode = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "dialogWidthThreshold", {
        /** Sets the threshold for which a dialog (for mobile viewports) is used to display the calendar instead of the standard popup. */
        set: function (value) {
            this._foundation.dialogWidthThreshold = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "notifyInputValueChanges", {
        /** Gets/sets whether the native input will be notified of value changes via the `input` and `change` events. */
        get: function () {
            return this._foundation.notifyInputValueChanges;
        },
        set: function (value) {
            this._foundation.notifyInputValueChanges = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "allowInvalidDate", {
        /** Gets/sets whether to allow an invalid date to be input. When true, the datepicker will not clear out the value of the input if the date was invalid (i.e. could not be parsed). */
        get: function () {
            return this._foundation.allowInvalidDate;
        },
        set: function (value) {
            this._foundation.allowInvalidDate = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "showToday", {
        /** Gets/sets whether the today button is visible in the popup. */
        get: function () {
            return this._foundation.showToday;
        },
        set: function (value) {
            this._foundation.showToday = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "showClear", {
        /** Gets/sets whether the clear button is visible in the popup. */
        get: function () {
            return this._foundation.showClear;
        },
        set: function (value) {
            this._foundation.showClear = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "parseCallback", {
        /** Sets the callback to use for parsing a date value string to a `Date` object. */
        get: function () {
            return this._foundation.parseCallback;
        },
        set: function (cb) {
            this._foundation.parseCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "formatCallback", {
        /** Sets the callback to use for formatting `Date` value to a custom string format. */
        get: function () {
            return this._foundation.formatCallback;
        },
        set: function (cb) {
            this._foundation.formatCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "prepareMaskCallback", {
        /** Sets the callback to use when altering default mask entry. */
        get: function () {
            return this._foundation.prepareMaskCallback;
        },
        set: function (cb) {
            this._foundation.prepareMaskCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DatepickerComponent.prototype, "disabledDaysOfWeek", {
        get: function () {
            return this._foundation.disabledDaysOfWeek;
        },
        set: function (value) {
            var trueValue = coerceNumberArray(value);
            this._foundation.disabledDaysOfWeek = trueValue;
        },
        enumerable: true,
        configurable: true
    });
    __decorate([
        FoundationProperty({ set: true })
    ], DatepickerComponent.prototype, "disableDayCallback", void 0);
    __decorate([
        FoundationProperty()
    ], DatepickerComponent.prototype, "startView", void 0);
    DatepickerComponent = __decorate([
        CustomElement({
            name: DATEPICKER_CONSTANTS.elementName,
            dependencies: [
                PopupComponent,
                CalendarComponent,
                DialogComponent,
                ToolbarComponent,
                IconButtonComponent,
                ScaffoldComponent,
                IconComponent,
                ButtonComponent
            ]
        })
    ], DatepickerComponent);
    return DatepickerComponent;
}(HTMLElement));
export { DatepickerComponent };
